import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useAuth } from '../../auth/auth-provider';
import {
  Dropdown,
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from '../ui/dropdown';
import { ThemeToggle } from '../ui/theme-toggle';
import { useSidebar } from '../../vendor/tailadmin/context/SidebarContext';
import { BackButton } from './back-button';
import { GlobalSearch } from './global-search';

/**
 * App header — fork of `vendor/tailadmin/layout/AppHeader.tsx`.
 *
 * MODIFIED FROM UPSTREAM:
 *   - `Link` from `react-router` → `@tanstack/react-router`.
 *   - Vendor `NotificationDropdown` (370 LOC of hardcoded demo
 *     notifications) REMOVED — TENA EDM has no notification feed yet
 *     (planned post-M3 with WebSocket events).
 *   - Vendor `UserDropdown` (162 LOC of hardcoded "Musharof Chowdhury"
 *     fake user) REPLACED by a real auth-aware dropdown ported from the
 *     in-house `components/nav/topbar.tsx` (email + role + theme +
 *     change-password + logout).
 *   - Vendor `ThemeToggleButton` REMOVED (depended on vendor
 *     `ThemeContext`); our `<ThemeToggle>` is used inside the user
 *     dropdown for theme switching, matching the existing UX.
 *   - Search input replaced by the real `<GlobalSearch>` component
 *     (#search) — Ctrl/Cmd+K fulltext across orders/offers/partners,
 *     wired to `GET /api/v1/search`.
 *   - Demo logo PNG `/images/logo/logo.svg` replaced by inline "TENA EDM"
 *     wordmark in brand colour.
 */
const roleLabels: Record<string, string> = {
  admin: 'Administrátor',
  decision_maker: 'Vedoucí',
  partner: 'Partner',
};

function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._\-]/).filter(Boolean);
  if (parts.length >= 2) {
    const [a, b] = parts;
    if (a && b) return (a.charAt(0) + b.charAt(0)).toUpperCase();
  }
  return (local.slice(0, 2) || '??').toUpperCase();
}

export function AppHeader() {
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const auth = useAuth();
  const navigate = useNavigate();
  const user = auth.user;

  const handleSidebarToggle = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  return (
    <header className="sticky top-0 z-99999 flex w-full border-gray-200 bg-white lg:border-b dark:border-gray-800 dark:bg-gray-900">
      <div className="flex grow flex-col items-center justify-between lg:flex-row lg:px-6">
        <div className="z-99999 flex w-full items-center justify-between gap-2 border-b border-gray-200 px-3 py-3 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4 dark:border-gray-800">
          <button
            type="button"
            className="z-99999 h-10 w-10 items-center justify-center rounded-lg border-gray-200 text-gray-500 lg:flex lg:h-11 lg:w-11 lg:border dark:border-gray-800 dark:text-gray-400"
            onClick={handleSidebarToggle}
            aria-label="Otevřít navigaci"
          >
            {isMobileOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="12"
                viewBox="0 0 16 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>

          <Link
            to="/"
            className="text-base font-semibold text-brand-700 lg:hidden dark:text-brand-400"
          >
            TENA EDM
          </Link>

          {/* Zpět tlačítko (#72190) — žije v headeru (sticky), takže zůstává
              viditelné při scrollování dlouhých stránek. Self-hides když není
              kam jít (viz BackButton). */}
          <BackButton />

          <button
            type="button"
            onClick={() => setApplicationMenuOpen((v) => !v)}
            className="z-99999 flex h-10 w-10 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 lg:hidden dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Přepnout menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <div className="hidden lg:block">
            <GlobalSearch />
          </div>
        </div>

        <div
          className={`${
            isApplicationMenuOpen ? 'flex' : 'hidden'
          } shadow-theme-md w-full items-center justify-between gap-4 px-5 py-4 lg:flex lg:justify-end lg:px-0 lg:shadow-none`}
        >
          {user ? (
            <Dropdown>
              <DropdownTrigger
                aria-label="Otevřít uživatelské menu"
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                >
                  {initialsFromEmail(user.email)}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block font-medium text-gray-800 dark:text-gray-100">
                    {user.email}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {roleLabels[user.role] ?? user.role}
                  </span>
                </span>
                <span aria-hidden="true" className="text-gray-400">
                  ▾
                </span>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownHeader>
                  <span className="block font-medium text-gray-800 dark:text-gray-100">
                    {user.email}
                  </span>
                  <span className="block text-gray-500 dark:text-gray-400">
                    {roleLabels[user.role] ?? user.role}
                  </span>
                </DropdownHeader>
                <DropdownSeparator />
                <div className="px-3 py-2">
                  <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">Barevný motiv</div>
                  <ThemeToggle />
                </div>
                <DropdownSeparator />
                <DropdownItem onSelect={() => void navigate({ to: '/profile/change-password' })}>
                  Změna hesla
                </DropdownItem>
                <DropdownSeparator />
                <DropdownItem
                  variant="danger"
                  onSelect={() => {
                    void auth.logout();
                  }}
                >
                  Odhlásit se
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          ) : (
            <Link to="/login" className="btn-secondary text-sm">
              Přihlásit
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
